import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { getBackendUrl, getSessionToken } from '@/lib/better-auth';
import { isNativePlatform } from '@/lib/native-features';
import { showLocalNotification } from './local-notification';

interface UseNotificationWebSocketOptions {
  userId: string | undefined;
  token: string | null | undefined;
  enabled: boolean;
}

const MAX_RECONNECT_ATTEMPTS = 10;
const BASE_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;

function getWebSocketBase(): string {
  const backendUrl = getBackendUrl();
  if (typeof window === 'undefined') return 'ws://localhost:34568';
  const url = new URL(backendUrl);
  const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  // On-prem WS runs on port 34568; cloud deployments don't have this
  // If the backend is localhost or a private IP, use port 34568
  // Otherwise, same port as HTTP (cloud setup)
  if (
    url.hostname === 'localhost' ||
    url.hostname === '127.0.0.1' ||
    url.hostname.endsWith('.local') ||
    /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(url.hostname)
  ) {
    // On-prem: use dedicated WS port
    return `${protocol}//${url.hostname}:34568`;
  }
  // Cloud: no WS notification channel (uses FCM instead)
  return '';
}

function jitter(baseMs: number): number {
  return baseMs + Math.random() * baseMs * 0.3;
}

export function useNotificationWebSocket(options: UseNotificationWebSocketOptions) {
  const { userId, token, enabled } = options;
  const queryClient = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const seenIdsRef = useRef<Set<string>>(new Set());
  const lastSeenIdRef = useRef<string | null>(null);
  const isConnectingRef = useRef(false);
  const intentionallyClosedRef = useRef(false);

  const invalidateNotificationQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
    queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
  }, [queryClient]);

  const handleNotification = useCallback(
    (msg: {
      notificationId: string;
      title: string;
      message: string;
      link?: string;
    }) => {
      // Deduplicate
      if (seenIdsRef.current.has(msg.notificationId)) return;
      seenIdsRef.current.add(msg.notificationId);
      lastSeenIdRef.current = msg.notificationId;

      // Trim seen set to prevent unbounded growth
      if (seenIdsRef.current.size > 200) {
        const entries = Array.from(seenIdsRef.current);
        seenIdsRef.current = new Set(entries.slice(-100));
      }

      // Invalidate React Query caches so notification tray refreshes
      invalidateNotificationQueries();

      // Show local notification
      const numericId =
        parseInt(msg.notificationId.replace(/[^a-zA-Z0-9]/g, '').slice(-6), 36) % 100000;
      showLocalNotification({
        id: numericId,
        title: msg.title,
        body: msg.message,
        data: {
          link: msg.link || '',
          notificationId: msg.notificationId,
        },
      });
    },
    [invalidateNotificationQueries],
  );

  const handleReconcile = useCallback(
    (msg: { notifications: Array<{ notificationId: string }> }) => {
      const notifications = msg.notifications || [];
      for (const notif of notifications) {
        if (!seenIdsRef.current.has(notif.notificationId)) {
          seenIdsRef.current.add(notif.notificationId);
          lastSeenIdRef.current = notif.notificationId;
        }
      }
      if (notifications.length > 0) {
        invalidateNotificationQueries();
      }
    },
    [invalidateNotificationQueries],
  );

  const connect = useCallback(() => {
    if (isConnectingRef.current || !enabled || !userId) return;

    const wsBase = getWebSocketBase();
    if (!wsBase) return; // Cloud deployment, no WS notification channel

    const existingWs = wsRef.current;
    if (
      existingWs &&
      (existingWs.readyState === WebSocket.OPEN ||
        existingWs.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    isConnectingRef.current = true;
    intentionallyClosedRef.current = false;

    let ws: WebSocket;
    try {
      ws = new WebSocket(wsBase);
    } catch (err) {
      // SecurityError: mixed content (HTTPS page + ws:// endpoint) or other WS construction failures
      console.warn('[NotifWS] Failed to create WebSocket:', err);
      isConnectingRef.current = false;
      return;
    }

    ws.onopen = () => {
      isConnectingRef.current = false;
      reconnectAttemptsRef.current = 0;

      // Authenticate with session token (or fallback to cached/device token on native)
      const authToken = token || getSessionToken();
      console.log('[NotifWS] Connected, token:', !!authToken, 'length:', authToken?.length ?? 0);
      if (authToken) {
        ws.send(JSON.stringify({ type: 'auth', token: authToken }));
      } else {
        console.warn('[NotifWS] No token available, closing');
        ws.close(1000, 'No session token');
      }
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data as string);

        switch (msg.type) {
          case 'auth':
            if (msg.success) {
              // Auth response from server — subscribe to notifications
              ws.send(JSON.stringify({ type: 'subscribe-notifications' }));

              // Reconcile missed notifications
              if (lastSeenIdRef.current) {
                ws.send(
                  JSON.stringify({
                    type: 'reconcile-notifications',
                    lastNotificationId: lastSeenIdRef.current,
                  }),
                );
              }
              console.log('[NotifWS] Connected and subscribed');
            } else if (msg.success === false) {
              // Explicit auth rejection from server
              console.warn('[NotifWS] Auth failed:', msg.error);
            }
            // else: server's initial auth request (no success field) — ignore
            break;
          case 'notification':
            handleNotification(msg);
            break;
          case 'notification-reconcile':
            handleReconcile(msg);
            break;
          case 'notification-subscribed':
            console.log('[NotifWS] Subscription confirmed');
            break;
          // Ignore chat-related messages on this connection
          case 'pong':
            break;
        }
      } catch (e) {
        console.error('[NotifWS] Parse error:', e);
      }
    };

    ws.onclose = (event: CloseEvent) => {
      isConnectingRef.current = false;
      wsRef.current = null;

      if (intentionallyClosedRef.current) return;
      if (event.code === 1000 || event.code === 1003 || event.code === 1008) return;

      // Reconnect with exponential backoff + jitter
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttemptsRef.current++;
        const delay = Math.min(
          BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current - 1),
          MAX_RECONNECT_DELAY,
        );
        reconnectTimeoutRef.current = setTimeout(() => connect(), jitter(delay));
      }
    };

    ws.onerror = () => {
      isConnectingRef.current = false;
    };

    wsRef.current = ws;
  }, [enabled, userId, handleNotification, handleReconcile]);

  const disconnect = useCallback(() => {
    intentionallyClosedRef.current = true;
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    if (wsRef.current) {
      wsRef.current.close(1000, 'Component unmount');
      wsRef.current = null;
    }
  }, []);

  // Connect when authenticated, disconnect on unmount
  useEffect(() => {
    if (enabled && userId) {
      connect();
    }
    return () => disconnect();
  }, [enabled, userId, connect, disconnect]);

  // On native: reconnect WS when app comes back to foreground (screen unlock).
  // OS suspends WebView when locked — WS dies silently without close event.
  useEffect(() => {
    if (!isNativePlatform() || !enabled || !userId) return;

    let cleanup: (() => void) | undefined;

    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handler = await App.addListener('appStateChange', (state) => {
          if (state.isActive) {
            // App resumed — check if WS is dead and reconnect
            const ws = wsRef.current;
            if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
              reconnectAttemptsRef.current = 0;
              connect();
            }
          }
        });
        cleanup = () => handler.remove();
      } catch {
        // App plugin not available
      }
    })();

    return () => cleanup?.();
  }, [enabled, userId, connect]);
}
